
import React, { useState, useEffect, useRef } from 'react';
import { nanoid } from 'nanoid';
import { toast } from 'sonner';
import { FlaskConical, Loader2 } from 'lucide-react';
import { isValidEIN, isValidEmail, formatEIN } from '@/utils/validation';
import { validatePhoneNumber } from '@/utils/phoneValidation';
import { Button } from '@/components/ui/button';
import { CompanyData } from '@/types';
import OrganisationFormFields from './OrganisationFormFields';
import TermsOfServiceModal from '@/components/TermsOfServiceModal';
import { isProductionUrl } from '@/utils/featureFlags';
import { generateTestClient } from '@/utils/testClientFixture';
import { isInvalidRevenue } from '@/utils/organization/revenue';

export interface OrganisationDetailsSubmitMeta {
  /** True when the admin used "Auto fill Test Client" and kept the fill. */
  isTestClient: boolean;
}

interface OrganisationDetailsFormProps {
  onSubmit: (data: CompanyData, meta: OrganisationDetailsSubmitMeta) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  initialData?: CompanyData | null;
  organizationId?: string;
  isAdminEdit?: boolean; // Add this prop to indicate admin edit mode
  /**
   * 'adminCreate' is Admin › Add Merchant (FIG-319): Company Information then
   * Point of Contact, no phone or Terms, and "Auto fill Test Client".
   */
  mode?: 'default' | 'adminCreate';
  /** Keeps the test-client fill when the admin comes back from step 2. */
  initialIsTestClient?: boolean;
  /** FIG-345 · UC-228: show the Point of Contact read-only (editing an existing merchant). */
  lockPointOfContact?: boolean;
}

const OrganisationDetailsForm: React.FC<OrganisationDetailsFormProps> = ({ 
  onSubmit, 
  onCancel,
  isSubmitting,
  initialData,
  organizationId,
  isAdminEdit = false, // Default to false for backward compatibility
  mode = 'default',
  initialIsTestClient = false,
  lockPointOfContact = false
}) => {
  const isAdminCreate = mode === 'adminCreate';
  // REQ-037 OPEN question: shown everywhere except production for now.
  const canAutofillTestClient = isAdminCreate && !isProductionUrl();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isTestClient, setIsTestClient] = useState(initialIsTestClient);
  const [isAutofilling, setIsAutofilling] = useState(false);
  // Bumped by Clear and by typing, so a slow auto-fill never overwrites them.
  const formGeneration = useRef(0);
  
  // Initialize form data using the structure needed by OrganisationFormFields
  const [formData, setFormData] = useState<CompanyData>({
    pocName: '',
    pocEmail: '',
    pocPhone: '',
    pocPhoneVerified: false,
    name: '',
    name_dbas: '',
    address: '',
    stateOfIncorporation: '',
    ein: '',
    products: [{ id: nanoid(), name: '', description: '', category: '' }],
    terms: false
  });

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      // Ensure products have IDs
      const productsWithIds = initialData.products.map(product => ({
        ...product,
        id: product.id || nanoid()
      }));
      
      setFormData({ 
        ...initialData,
        products: productsWithIds,
        pocPhone: initialData.pocPhone || '',
        pocPhoneVerified: initialData.pocPhoneVerified || false
      });
    }
  }, [initialData]);

  const [formErrors, setFormErrors] = useState({
    pocName: false,
    pocEmail: false,
    pocPhone: false,
    name: false,
    address: false,
    stateOfIncorporation: false,
    ein: false,
    revenueLast12Months: false,
    products: false,
    terms: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    formGeneration.current += 1;
    const { name, value } = e.target;
    
    const updatedFormData = {
      ...formData,
      [name]: name === 'ein' ? formatEIN(value) : value
    };
    
    setFormData(updatedFormData);
    
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors({ ...formErrors, [name]: false });
    }
  };

  const handlePhoneChange = (phone: string) => {
    setFormData({
      ...formData,
      pocPhone: phone,
      pocPhoneVerified: false // Reset verification when phone changes
    });
    
    if (formErrors.pocPhone) {
      setFormErrors({ ...formErrors, pocPhone: false });
    }
  };

  const handlePhoneVerificationComplete = (verified: boolean) => {
    setFormData({
      ...formData,
      pocPhoneVerified: verified
    });
    
    if (verified && formErrors.pocPhone) {
      setFormErrors({ ...formErrors, pocPhone: false });
    }
  };

  const handleProductChange = (id: string, field: keyof (typeof formData.products)[0], value: string) => {
    const updatedProducts = formData.products.map(product => 
      product.id === id ? { ...product, [field]: value } : product
    );
    
    setFormData({ 
      ...formData, 
      products: updatedProducts 
    });
    
    if (formErrors.products && updatedProducts.some(p => p.name && p.description && p.category)) {
      setFormErrors({ ...formErrors, products: false });
    }
  };

  const addProduct = () => {
    setFormData({
      ...formData,
      products: [...formData.products, { id: nanoid(), name: '', description: '', category: '' }],
    });
  };

  const removeProduct = (id: string) => {
    if (formData.products.length > 1) {
      setFormData({
        ...formData,
        products: formData.products.filter(product => product.id !== id),
      });
    } else {
      toast('You must have at least one product', {
        description: 'At least one product is required for the organization.'
      });
    }
  };

  const handleTermsCheckedChange = (checked: boolean) => {
    setFormData({
      ...formData,
      terms: checked
    });
  };

  const validateForm = (): boolean => {
    const newErrors = {
      // A locked POC isn't saved from this form, so it never blocks it (Greptile P1 on #291).
      pocName: !lockPointOfContact && !formData.pocName.trim(),
      pocEmail: !lockPointOfContact && !isValidEmail(formData.pocEmail),
      pocPhone: false, // Skip phone verification for admin edits
      name: !formData.name.trim(),
      address: !formData.address?.trim(),
      stateOfIncorporation: !formData.stateOfIncorporation,
      ein: !isValidEIN(formData.ein),
      revenueLast12Months: isInvalidRevenue(formData.revenueLast12Months),
      products: false, // Not validated in this simplified version
      // Terms are accepted by the contact at first sign-in when an admin adds the merchant.
      terms: isAdminCreate ? false : !formData.terms
    };

    setFormErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAdminCreate && !formData.terms) {
      toast('Terms of Service Required', {
        description: "You must accept the Terms of Service to continue"
      });
      return;
    }

    if (validateForm()) {
      // Include organization ID when submitting
      const submissionData = {
        ...formData,
        organization_id: organizationId || formData.organization_id
      };
      onSubmit(submissionData, { isTestClient: isAdminCreate && isTestClient });
    } else {
      toast('Validation Error', {
        description: "Please fix the highlighted fields and try again."
      });
    }
  };

  const handleAutofill = async () => {
    setIsAutofilling(true);
    const generation = ++formGeneration.current;
    try {
      const testClient = await generateTestClient();
      if (generation !== formGeneration.current) return;
      setFormData(testClient);
      setFormErrors({
        pocName: false, pocEmail: false, pocPhone: false, name: false, address: false,
        stateOfIncorporation: false, ein: false, revenueLast12Months: false, products: false, terms: false
      });
      setIsTestClient(true);
    } catch (error) {
      console.error('Could not auto fill the test client:', error);
      toast('Could not auto fill the test client', { description: 'Please try again.' });
    } finally {
      setIsAutofilling(false);
    }
  };

  const handleClearAutofill = () => {
    formGeneration.current += 1;
    setFormData({
      pocName: '', pocEmail: '', pocPhone: '', pocPhoneVerified: false, name: '', name_dbas: '',
      address: '', stateOfIncorporation: '', ein: '', revenueLast12Months: undefined,
      products: [{ id: nanoid(), name: '', description: '', category: '' }], terms: false
    });
    setIsTestClient(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-4">
      {canAutofillTestClient && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutofill}
            disabled={isAutofilling || isSubmitting}
            className="gap-2 border-dashed border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100"
          >
            {isAutofilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
            Auto fill Test Client
          </Button>
        </div>
      )}
      {isAdminCreate && isTestClient && (
        <div role="status" className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-amber-500 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="flex-1">
            Test client: filled with fake data and a unique EIN. It will be added with the [TEST] prefix and shown as Test.
          </span>
          <Button type="button" variant="link" size="sm" className="h-auto p-0 text-amber-900" onClick={handleClearAutofill}>
            Clear
          </Button>
        </div>
      )}
      <OrganisationFormFields
        formData={formData}
        formErrors={formErrors}
        handleInputChange={handleInputChange}
        handleProductChange={handleProductChange}
        addProduct={addProduct}
        removeProduct={removeProduct}
        onTermsCheckedChange={handleTermsCheckedChange}
        onPhoneChange={handlePhoneChange}
        onPhoneVerificationComplete={handlePhoneVerificationComplete}
        showTermsModal={showTermsModal}
        setShowTermsModal={setShowTermsModal}
        disableEin={organizationId ? true : false}
        isAdminEdit={isAdminEdit} // Pass the admin edit flag to form fields
        layout={isAdminCreate ? 'adminCreate' : 'default'}
        lockPointOfContact={lockPointOfContact}
      />
      
      <div className="flex justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Next Step'
          )}
        </Button>
      </div>
      
      <TermsOfServiceModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </form>
  );
};

export default OrganisationDetailsForm;
